import { create } from 'zustand';
import api, { postMultipart } from '../services/api.js';
import { resetConnection } from '../services/signalr.js';
import { getStorage } from '../storage.js';
import type { User, Server } from '../types/index.js';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isSysadmin: boolean;
  isGuest: boolean;
  initialized: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, displayName: string, inviteCode?: string) => Promise<void>;
  guestJoin: (code: string, username: string, displayName: string) => Promise<{ server: Server }>;
  upgradeAccount: (email: string, password: string) => Promise<void>;
  logout: () => void;
  initialize: () => Promise<void>;
  updateProfile: (data: { displayName?: string; bio?: string; status?: string }) => Promise<void>;
  updateAvatar: (file: File) => Promise<void>;
  setPresenceStatus: (status: number) => void;
}

const getSysadminFromToken = (token: string | null): boolean => {
  if (!token) return false;
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const json = JSON.parse(atob(base64));
    return json.sysadmin === true || json.sysadmin === 'true';
  } catch {
    return false;
  }
};

const getGuestFromToken = (token: string | null): boolean => {
  if (!token) return false;
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const json = JSON.parse(atob(base64));
    return json.isGuest === true || json.isGuest === 'true';
  } catch {
    return false;
  }
};

let preLogoutCallbacks: (() => void)[] = [];

/** Register a callback to run before auth state is cleared on logout.
 *  Returns an unsubscribe function. */
export function onBeforeLogout(cb: () => void): () => void {
  preLogoutCallbacks.push(cb);
  return () => { preLogoutCallbacks = preLogoutCallbacks.filter(c => c !== cb); };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: (() => { try { const u = getStorage().getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; } })(),
  token: (() => { try { return getStorage().getItem('token'); } catch { return null; } })(),
  refreshToken: (() => { try { return getStorage().getItem('refreshToken'); } catch { return null; } })(),
  isAuthenticated: (() => { try { const s = getStorage(); return !!(s.getItem('token') && s.getItem('user')); } catch { return false; } })(),
  isSysadmin: (() => { try { return getSysadminFromToken(getStorage().getItem('token')); } catch { return false; } })(),
  isGuest: (() => { try { return getGuestFromToken(getStorage().getItem('token')); } catch { return false; } })(),
  initialized: false,

  initialize: async () => {
    const storage = getStorage();
    const storedToken = storage.getItem('token');
    const storedUser = storage.getItem('user');
    if (!storedToken || !storedUser) {
      set({ initialized: true });
      return;
    }

    let savedUser: User;
    try {
      savedUser = JSON.parse(storedUser) as User;
    } catch {
      get().logout();
      set({ initialized: true });
      return;
    }

    set({
      token: storedToken,
      refreshToken: storage.getItem('refreshToken'),
      user: savedUser,
      isAuthenticated: true,
      isSysadmin: getSysadminFromToken(storedToken),
      isGuest: getGuestFromToken(storedToken),
    });
    try {
      const res = await api.get(`/auth/profile/${savedUser.id}`);
      const user = res.data;
      storage.setItem('user', JSON.stringify(user));
      set({ user });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        // User deleted from server — logout immediately.
        get().logout();
      } else if (status === 401) {
        // Only logout if refreshAccessToken already cleared the refresh token
        // (meaning auth is genuinely invalid). If the refresh token is still
        // in storage, the failure was transient — keep cached user data.
        if (!getStorage().getItem('refreshToken')) {
          get().logout();
        }
      }
    }
    set({ initialized: true });
  },

  login: async (username, password) => {
    const res = await api.post('/auth/login', { username, password });
    const { token, refreshToken, user } = res.data;
    const s = getStorage();
    s.setItem('token', token);
    s.setItem('refreshToken', refreshToken);
    s.setItem('user', JSON.stringify(user));
    set({ token, refreshToken, user, isAuthenticated: true, isSysadmin: getSysadminFromToken(token), isGuest: false });
  },

  register: async (username, email, password, displayName, inviteCode) => {
    const res = await api.post('/auth/register', { username, email, password, displayName, inviteCode });
    const { token, refreshToken, user } = res.data;
    const s = getStorage();
    s.setItem('token', token);
    s.setItem('refreshToken', refreshToken);
    s.setItem('user', JSON.stringify(user));
    set({ token, refreshToken, user, isAuthenticated: true, isSysadmin: getSysadminFromToken(token), isGuest: false });
  },

  guestJoin: async (code, username, displayName) => {
    const res = await api.post(`/invites/${code}/guest-join`, { username, displayName });
    const { token, refreshToken, user, server } = res.data;
    const s = getStorage();
    s.setItem('token', token);
    s.setItem('refreshToken', refreshToken);
    s.setItem('user', JSON.stringify(user));
    set({ token, refreshToken, user, isAuthenticated: true, isSysadmin: false, isGuest: true });
    return { server };
  },

  upgradeAccount: async (email, password) => {
    const res = await api.post('/auth/upgrade', { email, password });
    const { token, refreshToken, user } = res.data;
    const s = getStorage();
    s.setItem('token', token);
    s.setItem('refreshToken', refreshToken);
    s.setItem('user', JSON.stringify(user));
    set({ token, refreshToken, user, isGuest: false, isSysadmin: getSysadminFromToken(token) });
  },

  logout: () => {
    // Run pre-logout callbacks while auth token is still valid
    // (e.g. unregister push token from server)
    for (const cb of preLogoutCallbacks) {
      try { cb(); } catch { /* best-effort */ }
    }
    preLogoutCallbacks = [];

    const s = getStorage();
    const refreshToken = s.getItem('refreshToken');
    if (refreshToken) {
      api.post('/auth/logout', { refreshToken }).catch(() => undefined);
    }
    s.removeItem('token');
    s.removeItem('refreshToken');
    s.removeItem('user');
    resetConnection();
    set({ token: null, refreshToken: null, user: null, isAuthenticated: false, isSysadmin: false, isGuest: false, initialized: true });
  },

  updateProfile: async (data) => {
    const res = await api.put('/auth/profile', data);
    const user = res.data;
    getStorage().setItem('user', JSON.stringify(user));
    set({ user });
  },

  updateAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const user = await postMultipart<User>('/auth/avatar', formData);
    getStorage().setItem('user', JSON.stringify(user));
    set({ user });
  },

  setPresenceStatus: (status) => {
    const user = get().user;
    if (!user) return;
    const updatedUser = { ...user, presenceStatus: status };
    getStorage().setItem('user', JSON.stringify(updatedUser));
    set({ user: updatedUser });
  },
}));
